import React from "react";

import { ChangeEventTimeline } from "models";

import { AlertBanner, Typography, Message as message } from "components";

import { ChangeEventTypeBadge } from "pages/admin/verifications/components/changeEvents/ChangeEventTypeBadge";
import { fmtNum } from "pages/admin/verifications/utils/changeEventTimeline";

type ChangeEventHeaderProps = {
  data: ChangeEventTimeline;
  onTypeClick: (type: string) => void;
};

const Tile = ({ label, value, note }: { label: string; value: string; note: string }) => (
  <div className="ce-tile">
    <span className="ce-tile-label">{label}</span>
    <strong>{value}</strong>
    <small>{note}</small>
  </div>
);

export const ChangeEventHeader = ({ data, onTypeClick }: ChangeEventHeaderProps) => {
  const { balances, causationCount, eventCount, events, field, truncated, typeCounts, year } = data;

  const types = Object.keys(typeCounts).sort((a, b) => (typeCounts[b] ?? 0) - (typeCounts[a] ?? 0));
  const span = events.length ? `${events[events.length - 1].createdAt.slice(0, 10)} → ${events[0].createdAt.slice(0, 10)}` : "—";

  return (
    <div className="ce-head">
      <div className="ce-row">
        <Typography variant="h3">{field.name || "unnamed field"}</Typography>
        <span className="ce-mono ce-hint">{field.id}</span>
        <button
          type="button"
          className="ce-linkish"
          onClick={() => {
            navigator.clipboard.writeText(field.id).then(() => message({ content: "field id copied" }));
          }}
        >
          copy
        </button>
      </div>
      <div className="ce-sub">{`${field.farmName || "unknown farm"} · ${field.country || "—"} · ${fmtNum(field.size)} ha`}</div>

      <div className="ce-tiles">
        <Tile label="verification points" value={fmtNum(balances.settled)} note={String(year)} />
        <Tile label="pending points" value={fmtNum(balances.pending)} note={String(year)} />
        <Tile label="potential payout" value={`${fmtNum(balances.payout)} €`} note="latest" />
        <Tile label="events" value={String(eventCount)} note={`${types.length} types`} />
        <Tile label="causation links" value={String(causationCount)} note={causationCount ? "chain present" : "none yet"} />
        <Tile label="span" value={span} note="oldest → newest" />
      </div>

      <div className="ce-row ce-typelist">
        {types.map((type) => (
          <ChangeEventTypeBadge type={type} count={typeCounts[type]} key={type} onClick={() => onTypeClick(type)} />
        ))}
      </div>

      {!field.exists && (
        <AlertBanner
          type="warning"
          headingSize="paragraph-s"
          descriptionSize="paragraph-s"
          iconName="tabler/alert-triangle"
          iconSize={20}
          title="No farm_fields row for this id"
          description="change_events rows deliberately outlive the entities they describe, so this is the history of a field that no longer exists."
        />
      )}
      {(field.disabled || field.disabledBySplit) && (
        <AlertBanner
          type="warning"
          headingSize="paragraph-s"
          iconName="tabler/alert-triangle"
          iconSize={20}
          title={field.disabledBySplit ? "This field was split away and is disabled by split." : "This field is disabled."}
        />
      )}
      {truncated && (
        <AlertBanner
          type="warning"
          headingSize="paragraph-s"
          descriptionSize="paragraph-s"
          iconName="tabler/alert-triangle"
          iconSize={20}
          title="More events than the endpoint returns"
          description="The oldest rows were dropped, so the earliest balances and diffs here are incomplete."
        />
      )}
    </div>
  );
};
